// @flow
import { memo } from "react";
import styled, { withTheme, css } from "styled-components";
import { Button } from "@nested/component-library";
import { getImage } from "@nested/utils";
import { media } from "../../media";
import { Property, type PropertyType } from "../PropertyCarousel/Property";

const PropertiesWrapper = styled.div`
  padding: 40px 0px 0px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  flex-direction: row;
  max-width: 1180px;
  margin: auto;
  ${media.desktop`
    padding: 80px 0px 40px;
    padding-bottom: 40px;
  `}
`;

const PropertyCardHolder = styled.div`
  margin-bottom: 40px;
  width: 100%;
  ${media.tablet`
    width: calc(100% / 2);
  `}

  ${media.desktop`
    width: calc(100% / 4);
  `}
`;

const noResultsContainer = css`
  margin-bottom: 40px;
  text-align: center;
`;

const thinkingNestor = css`
  width: 150px;
`;

const sorryMessage = css`
  font-weight: 500;
  font-size: 18px;
  line-height: 24px;
  margin: 20px 0 0;
`;

const badPun = css`
  font-size: 16px;
  line-height: 24px;
  margin: 5px 0 0;
`;

const NoResults = ({ resetFilters }) => (
  <div css={noResultsContainer}>
    <img
      css={thinkingNestor}
      src={getImage("illustrations/nestor/nestor_thought_bubble.png")}
      alt="thinking owl"
    />
    <p css={sorryMessage}>Sorry, we can’t find any homes like&nbsp;that</p>
    <p css={badPun}>'Owl' find you something else...</p>
    <Button onClick={resetFilters} css="margin-top: 30px;">
      Reset filters
    </Button>
  </div>
);

export function detailsUrl(
  address: PropertyAddress,
  externalId: string,
): string {
  return `/for-sale/${address.outcode}/${formattedStreet(
    address.street,
  )}/${externalId}`;
}

function formattedStreet(street: string): string {
  return street.replace(/,/g, "").replace(/\W+/g, "-");
}

const PropertyCardWrapper = withTheme(({ propertyDetails }) => (
  <PropertyCardHolder>
    <Property {...propertyDetails} />
  </PropertyCardHolder>
));

type Props = {|
  properties: PropertyType[],
  loading?: boolean,
  resetFilters: () => void,
|};

/*
 * We use `memo` here in order to prevent rerenders immediately
 * after a filter is changed. This component will only re-render
 * when `properties` is a completely new list
 */
const PropertyList = memo<Props>(({ loading, properties, resetFilters }) => (
  <PropertiesWrapper>
    {properties.length === 0 && !loading && (
      <NoResults resetFilters={resetFilters} />
    )}
    {properties.map((propertyDetails) => (
      <PropertyCardWrapper
        propertyDetails={propertyDetails}
        key={propertyDetails.externalId}
      />
    ))}
  </PropertiesWrapper>
));

export { PropertyList };
